import { useContext, useEffect } from 'react';
import useSWR from 'swr';
import HttpStatus from 'http-status-codes';
import { UserWithPermissions } from './user';
import { useRouter } from 'next/router';
import { MultiTenantContext } from './contexts/MultiTenantContext';
import { getMultiTenantContextInitialState } from './contexts/MultiTenantContextContainer';
import { NO_AUTH_REQUIRED_PATH_PREFIXES } from '../utils/noLoginPaths';
import { useTopRightToast } from '../components/hooks/useTopRightToast';

const REDIRECTED_CODE = 333;

class CustomError extends Error {
    // @ts-expect-error - TS2564 - Property 'response' has no initializer and is not definitely assigned in the constructor.
    response: { status: number };
}

// Using fetch instead of Axios because we can configure the `redirect` param;
// the ones with Axios only work in the Node context (e.g. `maxRedirects`).
const fetcher = async (url: string) =>
    fetch(url, { redirect: 'manual' }).then(res => {
        if (res.type === 'opaqueredirect') {
            const error = new CustomError('An error occurred while fetching the data.');
            error.response = { status: REDIRECTED_CODE };
            error.message = 'Your token may have expired.';
            throw error;
        }
        return res.json();
    });

// eslint-disable-next-line max-len
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
const useUser = ({ redirectTo = '', redirectIfFound = false } = {}) => {
    const toast = useTopRightToast();
    const router = useRouter();

    // this hook is sometimes called outside the multiTenant context so grab what context can from local storage as a fallback
    const maybeMultiTenantContext = useContext(MultiTenantContext);
    const multiTenantContext =
        typeof maybeMultiTenantContext.tenantId !== 'undefined'
            ? maybeMultiTenantContext
            : getMultiTenantContextInitialState();

    const hasTenantContext = !!multiTenantContext.tenantId;
    const query = hasTenantContext
        ? `?multiTenant=${multiTenantContext.tenantId}&datavanter=${multiTenantContext.datavanter}`
        : '';
    const {
        data: user,
        error,
        mutate: mutateUser,
    } = useSWR<UserWithPermissions>(
        NO_AUTH_REQUIRED_PATH_PREFIXES.some(prefix => router.pathname.startsWith(prefix))
            ? null
            : '/api/current' + query,
        fetcher
    );

    useEffect(() => {
        if (error) {
            if (hasTenantContext) {
                let errMsg: string | undefined = undefined;
                const tenantName = multiTenantContext.tenantName;
                switch (error.response?.status) {
                    // Handle when Kong tries to redirect the user; it means
                    // their token has expired
                    case REDIRECTED_CODE:
                        // Have to handle redirect responses on the login page
                        // as we check to see if we have a user session
                        if (window.location.href.indexOf('login') === -1) {
                            window.location.href = '/logout';
                        }
                        return;
                    case HttpStatus.NOT_FOUND:
                        errMsg = 'Tenant not found';
                        break;
                    case HttpStatus.BAD_REQUEST:
                        errMsg = 'Multiple tenants found';
                        break;
                    case HttpStatus.INTERNAL_SERVER_ERROR:
                        errMsg = 'Internal server error';
                        break;
                    case error.response?.status === 0:
                        errMsg = 'Network error';
                        console.error(errMsg);
                        break;
                    default:
                        errMsg = 'Other error message';
                        console.error(errMsg);
                        break;
                }
                if (errMsg) {
                    const showError = () => {
                        toast.closeAll();
                        toast({
                            title: `Error loading tenant ${tenantName}`,
                            description: `${errMsg}. Select another tenant, or try again later.`,
                            status: 'error',
                            duration: 10000,
                            isClosable: true,
                            containerStyle: { width: '600px', position: 'absolute', top: '50px' },
                        });
                    };
                    // It can be called several times in a row due to the current design,
                    // which is difficult to change without breaking things unintentionally.
                    // Calling it too often leads to intermittent internal errors in Chakra useToast.
                    // Tried lodash.debounce, didn't help.
                    // Timeout solves the problem.
                    setTimeout(showError, 200);
                }
            } else {
                toast.closeAll();
            }
            if (
                router.pathname !== '/error' &&
                router.pathname !== '/_error' &&
                router.pathname !== '/login'
            ) {
                void router.push('/error');
            }
        } else {
            toast.closeAll();
        }
        // if no redirect needed, just return (example: already on /dashboard)
        // if user data not yet there (fetch in progress, logged in or not) then don't do anything yet
        if (!redirectTo || !user) return;

        if (
            // If redirectTo is set, redirect if the user was not found.
            (redirectTo && !redirectIfFound && !user?.isLoggedIn) ||
            // If redirectIfFound is also set, redirect if the user was found
            (redirectIfFound && user?.isLoggedIn)
        ) {
            void router.push(redirectTo);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, redirectIfFound, redirectTo, error, hasTenantContext, multiTenantContext?.tenantName]);

    if (error || router.pathname === '/onboarding/setup' || router.pathname.startsWith('/provider/upload')) {
        return { user: undefined, mutateUser };
    }
    return { user, mutateUser };
};

export default useUser;
